/**
 * Drift Status Badge Component
 *
 * Feature: 023-service-principal-registry
 * Task: T035 - Create DriftStatusBadge component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Visual indicator for drift status with appropriate colors and icons
 */

import React from 'react';
import { DriftStatus } from '../types';

interface DriftStatusBadgeProps {
  status: DriftStatus | null;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

/**
 * Status configuration with colors and icons
 */
const statusConfig: Record<
  DriftStatus,
  {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: string;
    severity: 'critical' | 'warning' | 'info' | 'success';
  }
> = {
  PERMISSION_MISSING: {
    label: 'Permission Missing',
    color: 'text-red-800 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    borderColor: 'border-red-300 dark:border-red-700',
    icon: '🚫',
    severity: 'critical',
  },
  PERMISSION_ELEVATED: {
    label: 'Permission Elevated',
    color: 'text-orange-800 dark:text-orange-300',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-orange-300 dark:border-orange-700',
    icon: '⬆️',
    severity: 'warning',
  },
  SP_DELETED: {
    label: 'SP Deleted',
    color: 'text-red-900 dark:text-red-300',
    bgColor: 'bg-red-200 dark:bg-red-900/30',
    borderColor: 'border-red-400 dark:border-red-700',
    icon: '💀',
    severity: 'critical',
  },
  UNTRACKED_PERMISSION: {
    label: 'Untracked',
    color: 'text-yellow-800 dark:text-yellow-300',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    borderColor: 'border-yellow-300 dark:border-yellow-700',
    icon: '❓',
    severity: 'warning',
  },
  FIX_FAILED: {
    label: 'Fix Failed',
    color: 'text-red-800 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    borderColor: 'border-red-300 dark:border-red-700',
    icon: '❌',
    severity: 'critical',
  },
  RESOLVED_AUTO: {
    label: 'Auto-Fixed',
    color: 'text-green-800 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-300 dark:border-green-700',
    icon: '✅',
    severity: 'success',
  },
  RESOLVED_MANUAL: {
    label: 'Manually Resolved',
    color: 'text-blue-800 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-300 dark:border-blue-700',
    icon: '✔️',
    severity: 'success',
  },
};

/**
 * Size classes for the badge
 */
const sizeClasses = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-1',
  lg: 'text-base px-3 py-1.5',
};

/**
 * Drift Status Badge Component
 */
export const DriftStatusBadge: React.FC<DriftStatusBadgeProps> = ({
  status,
  size = 'md',
  showIcon = true,
}) => {
  // No drift - show "OK" status
  if (!status) {
    return (
      <span
        className={`
          inline-flex items-center rounded-full font-medium
          bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] border border-[var(--color-border)]
          ${sizeClasses[size]}
        `}
      >
        {showIcon && <span className='mr-1'>✓</span>}
        OK
      </span>
    );
  }

  const config = statusConfig[status];

  if (!config) {
    return (
      <span
        className={`inline-flex items-center rounded-full font-medium bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] ${sizeClasses[size]}`}
      >
        {status}
      </span>
    );
  }

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium border
        ${config.bgColor} ${config.color} ${config.borderColor}
        ${sizeClasses[size]}
      `}
      title={`Drift Status: ${config.label}`}
    >
      {showIcon && <span className='mr-1'>{config.icon}</span>}
      {config.label}
    </span>
  );
};

/**
 * Get severity for a drift status (for sorting/filtering)
 */
export function getDriftSeverity(status: DriftStatus | null): number {
  if (!status) return 0;
  const config = statusConfig[status];
  if (!config) return 0;

  switch (config.severity) {
    case 'critical':
      return 4;
    case 'warning':
      return 3;
    case 'info':
      return 2;
    case 'success':
      return 1;
    default:
      return 0;
  }
}

/**
 * Check if a drift status is considered active (needs attention)
 */
export function isActiveDrift(status: DriftStatus | null): boolean {
  if (!status) return false;
  return [
    'PERMISSION_MISSING',
    'PERMISSION_ELEVATED',
    'SP_DELETED',
    'UNTRACKED_PERMISSION',
    'FIX_FAILED',
  ].includes(status);
}

export default DriftStatusBadge;
